import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageLoader, Spinner } from "@/components/Kit";
import { playSfx, playJingle } from "@/lib/sound";
import * as Icons from "lucide-react";
import { ArrowLeftRight, Repeat, Plus, Minus, X, Check, Inbox, PackageOpen, Trash2, HandCoins } from "lucide-react";

function toPascal(s) { return (s || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(""); }
const RARITY = { Junk: "#8892a6", Common: "#8892a6", Uncommon: "#2D9CFF", Rare: "#B14DFF", "Ultra-Rare": "#FF2D55", Legendary: "#FFC531" };

function Chip({ it }) {
  const Icon = Icons[toPascal(it.icon)] || Icons.Package;
  const rc = RARITY[it.rarity] || "#8892a6";
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-[var(--radius)] bg-muted border border-border text-xs" data-testid={`chip-${it.item_id}`}>
      <Icon className="w-3.5 h-3.5" style={{ color: rc }} /> {it.name}{it.qty > 1 ? ` x${it.qty}` : ""}
    </span>
  );
}

// selection = { item_id: qty }; pool = array of item objects with .id/.item_id, .name, .icon, .rarity, .qty(max, optional)
function Picker({ pool, sel, setSel, testPrefix, capField }) {
  const inc = (id, max) => setSel((s) => ({ ...s, [id]: Math.min(max ?? 99, (s[id] || 0) + 1) }));
  const dec = (id) => setSel((s) => { const q = (s[id] || 0) - 1; const n = { ...s }; if (q <= 0) delete n[id]; else n[id] = q; return n; });
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-52 overflow-y-auto pr-1">
      {pool.length === 0 && <p className="col-span-full text-xs text-muted-foreground py-3 text-center">Nothing available.</p>}
      {pool.map((i) => {
        const id = i.item_id || i.id;
        const Icon = Icons[toPascal(i.icon)] || Icons.Package;
        const rc = RARITY[i.rarity] || "#8892a6";
        const q = sel[id] || 0;
        const max = capField ? i[capField] : undefined;
        return (
          <div key={id} data-testid={`${testPrefix}-${id}`} className={`panel p-2 border ${q ? "border-primary" : "border-border"}`}>
            <div className="flex items-center gap-1.5"><Icon className="w-4 h-4 shrink-0" style={{ color: rc }} /><span className="text-xs font-bold truncate">{i.name}</span></div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="label-cap text-[9px]">{max != null ? `own ${max}` : i.rarity}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => dec(id)} data-testid={`${testPrefix}-dec-${id}`} className="w-6 h-6 grid place-items-center rounded bg-muted hover:bg-border"><Minus className="w-3 h-3" /></button>
                <span className="mono text-xs w-4 text-center">{q}</span>
                <button onClick={() => inc(id, max)} data-testid={`${testPrefix}-inc-${id}`} className="w-6 h-6 grid place-items-center rounded bg-primary/20 text-primary hover:bg-primary/30"><Plus className="w-3 h-3" /></button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const toItems = (sel) => Object.entries(sel).map(([item_id, qty]) => ({ item_id, qty }));

export default function TradingPost() {
  const { refreshOffers } = useOutletContext();
  const [tab, setTab] = useState("browse");
  const [mine, setMine] = useState(false);
  const [trades, setTrades] = useState(null);
  const [inv, setInv] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(null);
  // create builder
  const [give, setGive] = useState({});
  const [want, setWant] = useState({});
  const [note, setNote] = useState("");
  // per-card expand
  const [active, setActive] = useState(null); // { tid, mode: 'make'|'offers' }
  const [offerSel, setOfferSel] = useState({});
  const [offerBits, setOfferBits] = useState("");
  const [offers, setOffers] = useState([]);

  const load = useCallback(async () => {
    const [t, i, s] = await Promise.all([api.get(`/trades${mine ? "?mine=1" : ""}`), api.get("/inventory"), api.get("/shop")]);
    setTrades(t.data.trades); setInv(i.data.items); setCatalog(s.data.items);
  }, [mine]);
  useEffect(() => { load(); }, [load]);
  if (!trades || !inv) return <PageLoader label="Opening the Trading Post" />;

  const tradeable = inv.filter((i) => i.category !== "scrap");

  const create = async () => {
    const g = toItems(give);
    if (g.length === 0) { toast.error("Pick at least one item to offer."); return; }
    setBusy("create");
    try {
      await api.post("/trades/create", { give: g, want: toItems(want), note });
      playSfx("pop"); toast.success("Trade posted!");
      setGive({}); setWant({}); setNote(""); setTab("browse"); await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };

  const openMake = (tid) => { setActive({ tid, mode: "make" }); setOfferSel({}); setOfferBits(""); };
  const submitOffer = async (tid) => {
    const items = toItems(offerSel);
    const bits = Math.max(0, parseInt(offerBits || "0", 10) || 0);
    if (items.length === 0 && bits <= 0) { toast.error("Offer items and/or Bits."); return; }
    setBusy(tid);
    try {
      await api.post(`/trades/${tid}/offer`, { items, bits });
      playSfx("coin"); toast.success("Offer sent! The owner decides.");
      setActive(null); setOfferSel({}); setOfferBits(""); await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };

  const openOffers = async (tid) => {
    setActive({ tid, mode: "offers" });
    const { data } = await api.get(`/trades/${tid}/offers`); setOffers(data.offers);
  };
  const accept = async (tid, oid) => {
    setBusy(oid);
    try { await api.post(`/trades/offers/${oid}/accept`, {}); playJingle(); toast.success("Trade complete — gear swapped!"); setActive(null); await load(); refreshOffers?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const decline = async (tid, oid) => {
    setBusy(oid);
    try { await api.post(`/trades/offers/${oid}/decline`, {}); toast.success("Offer declined, items returned."); const { data } = await api.get(`/trades/${tid}/offers`); setOffers(data.offers); await load(); refreshOffers?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const cancel = async (tid) => {
    setBusy(tid);
    try { await api.post(`/trades/${tid}/cancel`, {}); toast.success("Trade cancelled, items returned."); setActive(null); await load(); refreshOffers?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };

  return (
    <div data-testid="trading-page">
      <span className="label-cap text-primary flex items-center gap-2"><ArrowLeftRight className="w-4 h-4" /> Trading Post // Barter Broker</span>
      <h1 className="text-4xl font-black tracking-tighter mt-2 font-head">Trading Post</h1>
      <p className="text-muted-foreground mb-5">Bundle up to 10 items into a public lot with a description. Other pilots submit counter-offers of items <b className="text-foreground">and/or Bits</b> — accept the best one. Everything is escrowed until a deal closes.</p>

      <div className="flex gap-1 mb-5 p-1 rounded-[var(--radius)] bg-muted w-fit">
        {[["browse", `Open Trades (${trades.length})`], ["create", "Post a Trade"]].map(([id, l]) => (
          <button key={id} data-testid={`trade-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-[var(--radius)] text-xs font-bold uppercase tracking-wider mono ${tab === id ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "browse" ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <button data-testid="filter-all" onClick={() => { setMine(false); setActive(null); }}
              className={`px-3 py-1.5 rounded-[var(--radius)] text-xs font-bold uppercase mono ${!mine ? "bg-primary/20 text-primary border border-primary/40" : "bg-muted text-muted-foreground border border-border"}`}>All Trades</button>
            <button data-testid="filter-mine" onClick={() => { setMine(true); setActive(null); }}
              className={`px-3 py-1.5 rounded-[var(--radius)] text-xs font-bold uppercase mono ${mine ? "bg-secondary/20 text-secondary border border-secondary/40" : "bg-muted text-muted-foreground border border-border"}`}>My Trades</button>
          </div>
          {trades.length === 0 && <div className="panel p-8 text-center text-muted-foreground">{mine ? "You have no open trades. Post one from the 'Post a Trade' tab." : "No open trades. Post the first one!"}</div>}
          {trades.map((t) => {
            const isActive = active?.tid === t.trade_id;
            return (
              <div key={t.trade_id} data-testid={`trade-${t.trade_id}`} className="panel p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-head font-bold text-sm">{t.creator_name}</span>
                      {t.mine && <span className="label-cap text-[9px] text-secondary">YOUR TRADE</span>}
                      {t.offer_count > 0 && <span className="label-cap text-[9px] text-accent flex items-center gap-1"><Inbox className="w-3 h-3" />{t.offer_count} offer{t.offer_count > 1 ? "s" : ""}</span>}
                    </div>
                    {t.note && <p data-testid={`trade-note-${t.trade_id}`} className="text-xs text-muted-foreground italic mb-2">"{t.note}"</p>}
                    <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-3 items-center">
                      <div>
                        <div className="label-cap text-[9px] mb-1 text-secondary">Gives</div>
                        <div className="flex flex-wrap gap-1.5">{t.give.map((i) => <Chip key={i.item_id} it={i} />)}</div>
                      </div>
                      <Repeat className="w-5 h-5 text-primary mx-auto hidden sm:block" />
                      <div>
                        <div className="label-cap text-[9px] mb-1 text-accent">Wants</div>
                        <div className="flex flex-wrap gap-1.5">{t.want.length ? t.want.map((i) => <Chip key={i.item_id} it={i} />) : <span className="text-xs text-muted-foreground italic">Open to any offer</span>}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {t.mine ? (
                      <>
                        <button onClick={() => (isActive ? setActive(null) : openOffers(t.trade_id))} data-testid={`view-offers-${t.trade_id}`} className="btn-primary text-xs">{isActive && active.mode === "offers" ? <><X className="w-3.5 h-3.5" /> Close</> : <><Inbox className="w-3.5 h-3.5" /> Offers ({t.offer_count})</>}</button>
                        <button onClick={() => cancel(t.trade_id)} data-testid={`cancel-trade-${t.trade_id}`} disabled={busy === t.trade_id} className="btn-ghost text-xs"><Trash2 className="w-3.5 h-3.5" /> Cancel</button>
                      </>
                    ) : (
                      <button onClick={() => (isActive ? setActive(null) : openMake(t.trade_id))} data-testid={`make-offer-${t.trade_id}`} className="btn-secondary text-xs">{isActive && active.mode === "make" ? <><X className="w-3.5 h-3.5" /> Close</> : <><HandCoins className="w-3.5 h-3.5" /> Make Offer</>}</button>
                    )}
                  </div>
                </div>

                {isActive && active.mode === "make" && (
                  <div className="mt-4 pt-4 border-t border-border" data-testid={`offer-builder-${t.trade_id}`}>
                    <div className="label-cap mb-2">Your Offer — pick gear from your inventory</div>
                    <Picker pool={tradeable} sel={offerSel} setSel={setOfferSel} testPrefix="offer-pick" capField="qty" />
                    <div className="flex items-center gap-2 mt-3">
                      <HandCoins className="w-4 h-4 text-secondary shrink-0" />
                      <input type="number" min="0" value={offerBits} onChange={(e) => setOfferBits(e.target.value)}
                        data-testid={`offer-bits-${t.trade_id}`} placeholder="+ Bits to sweeten the offer (optional)" className="input-field flex-1 text-sm py-2" />
                    </div>
                    <button onClick={() => submitOffer(t.trade_id)} data-testid={`send-offer-${t.trade_id}`} disabled={busy === t.trade_id} className="btn-primary mt-3 w-full">
                      {busy === t.trade_id ? <Spinner className="w-4 h-4" /> : <><Check className="w-4 h-4" /> Send Offer</>}
                    </button>
                  </div>
                )}

                {isActive && active.mode === "offers" && (
                  <div className="mt-4 pt-4 border-t border-border space-y-2" data-testid={`offers-list-${t.trade_id}`}>
                    {offers.length === 0 && <p className="text-xs text-muted-foreground">No offers yet.</p>}
                    {offers.map((o) => (
                      <div key={o.offer_id} data-testid={`offer-${o.offer_id}`} className="panel p-3 flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <div className="text-xs font-bold mb-1">{o.offerer_name} offers:</div>
                          <div className="flex flex-wrap gap-1.5 items-center">{o.items.map((i) => <Chip key={i.item_id} it={i} />)}
                            {o.bits > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-[var(--radius)] bg-secondary/15 border border-secondary/40 text-xs text-secondary font-bold"><HandCoins className="w-3.5 h-3.5" />{o.bits} Bits</span>}
                            {o.items.length === 0 && o.bits === 0 && <span className="text-xs italic text-muted-foreground">nothing</span>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => accept(t.trade_id, o.offer_id)} data-testid={`accept-${o.offer_id}`} disabled={busy === o.offer_id} className="btn-primary text-xs"><Check className="w-3.5 h-3.5" /> Accept</button>
                          <button onClick={() => decline(t.trade_id, o.offer_id)} data-testid={`decline-${o.offer_id}`} disabled={busy === o.offer_id} className="btn-ghost text-xs"><X className="w-3.5 h-3.5" /> Decline</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="max-w-2xl grid md:grid-cols-2 gap-4">
          <div className="panel p-4">
            <div className="label-cap mb-2 flex items-center gap-1.5 text-secondary"><PackageOpen className="w-3.5 h-3.5" /> You Give (from inventory)</div>
            <Picker pool={tradeable} sel={give} setSel={setGive} testPrefix="give-pick" capField="qty" />
          </div>
          <div className="panel p-4">
            <div className="label-cap mb-2 flex items-center gap-1.5 text-accent"><Inbox className="w-3.5 h-3.5" /> You Want (wishlist)</div>
            <Picker pool={catalog} sel={want} setSel={setWant} testPrefix="want-pick" />
          </div>
          <div className="panel p-4 md:col-span-2">
            <div className="label-cap mb-2">Lot Description (optional)</div>
            <textarea data-testid="trade-note-input" value={note} onChange={(e) => setNote(e.target.value.slice(0, 280))} rows={2}
              placeholder="Describe your lot or what counter-offers you'll accept (e.g. 'LF Rare weapons or 500+ Bits')" className="input-field w-full resize-none text-sm" />
            <div className="text-right label-cap text-[9px]">{note.length}/280</div>
          </div>
          <button onClick={create} data-testid="post-trade-btn" disabled={busy === "create"} className="btn-primary md:col-span-2 py-3">
            {busy === "create" ? <Spinner className="w-4 h-4" /> : <><ArrowLeftRight className="w-4 h-4" /> Post Trade</>}
          </button>
          <p className="md:col-span-2 text-xs text-muted-foreground">Leaving the wishlist empty means "open to any offer". Your offered items are escrowed until the trade closes or you cancel.</p>
        </div>
      )}
    </div>
  );
}
